import BigNumber from "bignumber.js";
import { useEffect, useMemo, useState } from "react";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey, Transaction } from "@solana/web3.js";
import { getAssociatedTokenAddressSync } from "@solana/spl-token";
import Button from "@axvdex/components/common/Button";
import { useAppDispatch, useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import CustomSelect from "@axvdex/components/form-element/CustomSelect";
import imgSanitize from "@axvdex/utils/imgSanitize";
import useLanguage from "@axvdex/hooks/useLanguage";
import { getSale } from "@axvdex/hooks/solana/useOutbid";
import { OutbidAsset, Sale } from "@axvdex/types/solana/outbid";
import { sendToast } from "@axvdex/state/wallet/walletSlice";
import CustomLoader from "@axvdex/components/common/CustomLoader";
import {
  createEndSaleProcessInstruction,
  createUpdateConfigInstruction,
  errorFromCode,
} from "@axvdex/solana/outbidAPI/js/generated";
import { findTransferAuthority } from "@axvdex/hooks/solana/utils/DeriveOutbidPDA";
import { sendWithPhantomAndDebugTransaction } from "@axvdex/hooks/solana/utils/client";
import { SolanaSaleOperatorControlsProps } from "../SolanaSaleOperatorControls";

function EndProcess({ outbidSale }: SolanaSaleOperatorControlsProps) {
  const walletInfo = useAppSelector(selectWalletInfo);
  const [targetAsset, setTargetAsset] = useState(null);
  const { i18 } = useLanguage();
  const { connection } = useConnection();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDone, setLoadingDone] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [sales, setSales] = useState<Sale[]>([]);
  const [allSaleEnded, setAllSaleEnded] = useState(false);
  const dispatch = useAppDispatch();

  const numberOfTokens = useMemo(() => {
    let totalTargetAllocation = BigNumber(0);
    for (const sale of sales) {
      // TODO: Add | Doesn't exist on Solana sale.config.addedBonusAllocationTrigger
      const decimals = Math.pow(10, sale.config.targetAllocation.targetAssetDecimals.toNumber());
      const assetAllocation = sale.config.targetAllocation.targetAssetAllocationAmount.div(decimals);
      console.log(assetAllocation);
      totalTargetAllocation = totalTargetAllocation.plus(assetAllocation);
    }
    return totalTargetAllocation;
  }, [sales, isLoading]);

  const numberOfTokensDue = useMemo(() => {
    console.log("Recalculating total due");
    let totalTargetAllocation = BigNumber(0);
    for (const sale of sales) {
      if (sale.config.isProcessEnded) continue;
      // TODO: Bonus bidding. Doesn't exist on Solana sale.config.addedBonusAllocationTrigger
      const decimals = Math.pow(10, sale.config.targetAllocation.targetAssetDecimals.toNumber());
      const assetAllocation = sale.config.targetAllocation.targetAssetAllocationAmount.div(decimals);
      totalTargetAllocation = totalTargetAllocation.plus(assetAllocation);
    }
    return totalTargetAllocation;
  }, [sales, isLoading]);

  useEffect(() => {
    const fetch = async () => {
      console.log("Fetching sales");
      try {
        setIsFetching(true);
        const sales = await Promise.all(
          Object.keys(outbidSale.salesState).map(async saleAddr => {
            const sale = await getSale(connection, new PublicKey(saleAddr));
            if (!sale) {
              throw new Error("Sale not found");
            }
            return sale;
          })
        );
        setSales(sales);
        const allEnded = sales.every(sale => sale.config.isProcessEnded);
        setAllSaleEnded(allEnded);
      } catch (err) {
        const msg = `Failed to retrieve sales: ${err}`;
        dispatch(sendToast({ type: msg, info: { msg, toastID: "" + new Date().getTime() } }));
      } finally {
        setIsFetching(false);
      }
    };
    fetch();
  }, [outbidSale, loadingDone]);

  if (isFetching) {
    return (
      <div style={{ marginTop: "2em" }}>
        <CustomLoader size="sm"></CustomLoader>;
      </div>
    );
  }

  if (sales.length === 0) {
    return "No sales found.";
  }

  return (
    <div style={{ marginTop: "1em" }}>
      {sales.every(sale => sale.config.targetAllocation.targetAssetMint) ? (
        <>
          <p>
            Amount of the target asset to be transferred to the contract:
            {numberOfTokensDue.toString()}
            <img
              style={{ width: "1em", height: "1em", marginLeft: "0.5em" }}
              src={imgSanitize(outbidSale.assets[sales[0].config.targetAllocation.targetAssetMint.toBase58()].symbol)}
              alt="Target Asset Icon"
            />
          </p>
          <p>
            Total amount of the target asset to be transferred to the bidder:
            {numberOfTokens.toString()}
            <img
              style={{ width: "1em", height: "1em", marginLeft: "0.5em" }}
              src={imgSanitize(outbidSale.assets[sales[0].config.targetAllocation.targetAssetMint.toBase58()].symbol)}
              alt="Target Asset Icon"
            />
          </p>
          <Button
            btnColor="gradient"
            disabled={isLoading || allSaleEnded}
            onClick={async () => {
              setIsLoading(true);
              try {
                const tx = new Transaction();
                for (const sale of sales) {
                  // End process is already triggered
                  if (sale.config.isProcessEnded) continue;

                  // Build the instruction to starting the vesting process
                  const [transferAuthority] = findTransferAuthority(sale.saleAccount);
                  const destTokenAccount = getAssociatedTokenAddressSync(
                    sale.config.targetAllocation.targetAssetMint,
                    transferAuthority,
                    true
                  );
                  const srcTokenAccount = getAssociatedTokenAddressSync(
                    sale.config.targetAllocation.targetAssetMint,
                    window.solanaClient.publicKey,
                    true
                  );
                  const endIx = createEndSaleProcessInstruction({
                    saleAccount: sale.saleAccount,
                    saleAuthority: window.solanaClient.publicKey,
                    transferAuthority: transferAuthority,
                    targetAssetDest: destTokenAccount,
                    targetAssetSrc: srcTokenAccount,
                    targetAssetMint: sale.config.targetAllocation.targetAssetMint,
                  });
                  tx.add(endIx);
                }

                // Send the transaction
                // const signature = await sendWithPhantomAndDebugTransaction(tx, connection, errorFromCode);
                const signature = await window.solanaClient.sendTransaction(tx, connection);
                await connection.confirmTransaction(signature, "finalized");
                const msg = "All sale are ended successfully.";
                dispatch(sendToast({ type: "tx-success", info: { msg, toastID: "" + new Date().getTime() } }));
              } catch (error: unknown) {
                console.error(error);
                const msg = error instanceof Error ? error.message : "An error occurred";
                dispatch(sendToast({ type: "tx-fail", info: { msg, toastID: "" + new Date().getTime() } }));
              } finally {
                setIsLoading(false);
                setLoadingDone(true);
              }
            }}
          >
            {isLoading ? "Executing ..." : allSaleEnded ? "Already Ended" : "End Process"}
          </Button>
        </>
      ) : walletInfo.walletAddress ===
        outbidSale.saleAuthority /* Replace with: outbidSale.config.owner No Owner feature on Outbid yet */ ? (
        <>
          <h1 style={{ marginTop: "1em" }}>Set Target Asset Info</h1>
          <CustomSelect
            labelText={""}
            hiddenLabel={true}
            items={Object.values(outbidSale.assets)
              .map((asset: OutbidAsset) => {
                return {
                  value: asset.mint.toBase58(),
                  label: asset.symbol,
                  decimals: asset.decimals,
                  id: asset.mint.toBase58(),
                  optionPrefix: <img src={imgSanitize(asset.symbol)} alt={""} />,
                };
              })
              .filter(asset => asset)}
            placeholder={i18("Select token", "trade.form.fromToken.select.placeholder")}
            value={targetAsset}
            onChange={val => setTargetAsset(val)}
            name={""}
          />
          {targetAsset && (
            <Button
              style={{ marginTop: "1em" }}
              btnColor="gradient"
              disabled={isLoading}
              onClick={async () => {
                setIsLoading(true);
                try {
                  const transaction = new Transaction();
                  // Update the target asset info for all sales
                  for (const sale of sales) {
                    const [transferAuthority] = findTransferAuthority(sale.saleAccount);
                    const updateIx = createUpdateConfigInstruction(
                      {
                        saleAccount: sale.saleAccount,
                        saleAuthority: window.solanaClient.publicKey,
                        transferAuthority: transferAuthority,
                      },
                      {
                        updateConfigArgs: {
                          targetAssetAllocationAmount: null,
                          targetAssetDecimals: targetAsset.decimals,
                          targetTicketAmount: null,
                          targetAssetMint: new PublicKey(targetAsset.id),
                          minTicket: null,
                          maxTicket: null,
                          duration: null,
                          whitelistDuration: null,
                          bonusDuration: null,
                          bonusMinTicketIncrease: null,
                          bonusSellOutEligibleTokenPer: null,
                          bonusSellOutBonusTokenPer: null,
                          walletMaxTicketCap: null,
                          bidAssets: [],
                          isStopped: false,
                        },
                      }
                    );
                    transaction.add(updateIx);
                  }
                  // Send the transaction
                  // const signature = await sendWithPhantomAndDebugTransaction(transaction, connection, errorFromCode);
                  const signature = await window.solanaClient.sendTransaction(transaction, connection);
                  await connection.confirmTransaction(signature, "finalized");
                  const msg = "Asset target info updated successfully.";
                  dispatch(sendToast({ type: "tx-success", info: { msg, toastID: "" + new Date().getTime() } }));
                } catch (error: unknown) {
                  console.error(error);
                  const msg = error instanceof Error ? error.message : "An error occurred";
                  dispatch(sendToast({ type: "tx-fail", info: { msg, toastID: "" + new Date().getTime() } }));
                } finally {
                  setIsLoading(false);
                  setLoadingDone(true);
                }
              }}
            >
              {isLoading ? "Executing ..." : "Config Target Info"}
            </Button>
          )}
        </>
      ) : (
        "No target asset set. Please contact the owner to set a target asset."
      )}
    </div>
  );
}

export default EndProcess;
